import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { qualityApi, type QualityDocItem } from '../services/quality';

function chapterSortValue(chapter: string | null): number[] {
  return String(chapter ?? '')
    .split('.')
    .map((part) => Number.parseInt(part || '0', 10) || 0);
}

function compareChapter(a: string | null, b: string | null): number {
  const aa = chapterSortValue(a);
  const bb = chapterSortValue(b);
  const length = Math.max(aa.length, bb.length);
  for (let index = 0; index < length; index += 1) {
    const diff = (aa[index] ?? 0) - (bb[index] ?? 0);
    if (diff !== 0) {
      return diff;
    }
  }
  return 0;
}

export function QualityDocsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<QualityDocItem[]>([]);

  useEffect(() => {
    qualityApi.docs()
      .then((data) => setItems(data.items))
      .catch(() => toast.error('Kunde inte ladda kvalitetspärmen'))
      .finally(() => setLoading(false));
  }, [toast]);

  const sortedItems = useMemo(
    () => [...items].sort((left, right) => compareChapter(left.chapter, right.chapter) || left.id - right.id),
    [items],
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-3">
          <Link to="/quality" className="inline-flex items-center rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50">
            Tillbaka
          </Link>
          <div className="space-y-1">
            <h1 className="text-[26px] font-semibold tracking-[-0.03em] text-slate-950 md:text-[30px]">Kvalitetspärm</h1>
          </div>
        </div>

        <Link
          to="/quality/docs/print"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
        >
          Skriv ut / PDF
        </Link>
      </div>

      <div className="overflow-hidden rounded-[28px] border border-slate-300 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
        <div className="max-h-[calc(100dvh-250px)] overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="border-b border-slate-300 text-left text-slate-900">
                <th className="px-4 py-3 font-semibold">Kapitel</th>
                <th className="px-4 py-3 font-semibold">Titel</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={2} className="px-4 py-8 text-center text-slate-500">Laddar kvalitetspärmen...</td>
                </tr>
              ) : sortedItems.map((item) => (
                <tr
                  key={item.id}
                  className="cursor-pointer border-b border-slate-200 transition hover:bg-amber-50 last:border-b-0"
                  onClick={() => navigate(`/quality/docs/${item.id}`)}
                >
                  <td className="px-4 py-3 font-medium text-slate-900">{item.chapter ?? '-'}</td>
                  <td className="px-4 py-3 text-slate-800">{item.title}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
